import React, { createContext, useContext, useState, useMemo, useEffect } from "react";
import { Step, StepperContextType } from "./types";

const StepperContext = createContext<StepperContextType | undefined>(undefined);

export const useStepperContext = () => {
  const context = useContext(StepperContext);
  if (!context) {
    throw new Error("useStepperContext must be used within a StepperProvider");
  }
  return context;
};

interface StepperProviderProps {
  steps: Step[];
  initialStep?: number;
  onStepChange?: (step: number) => void;
  onStepComplete?: (stepId: string, data: any) => void;
  onComplete?: () => void;
  children: React.ReactNode;
}

export const StepperProvider: React.FC<StepperProviderProps> = ({
  steps,
  initialStep = 0,
  onStepChange,
  onStepComplete,
  onComplete,
  children,
}) => {
  const [activeStep, setActiveStepState] = useState(initialStep);
  const [stepsState, setStepsState] = useState<Step[]>(steps);
  const [stepData, setStepDataState] = useState<Record<string, any>>({});
  
  // Update steps when they change from the parent
  useEffect(() => {
    setStepsState(steps);
  }, [steps]);

  const setActiveStep = (step: number) => {
    if (step >= 0 && step < steps.length) {
      setActiveStepState(step);
      if (onStepChange) {
        onStepChange(step);
      }
    }
  };
  
  const completeStep = (stepId: string, data?: any) => {
    const stepIndex = stepsState.findIndex((step) => step.id === stepId);
    if (stepIndex !== -1) {
      const updatedSteps = [...stepsState];
      updatedSteps[stepIndex] = {
        ...updatedSteps[stepIndex],
        isCompleted: true,
      };
      setStepsState(updatedSteps);
      
      if (data) {
        setStepData(stepId, data);
      }
      
      if (onStepComplete) {
        onStepComplete(stepId, data || stepData[stepId]);
      }
      
      // If this is the last step, call onComplete
      if (stepIndex === steps.length - 1 && onComplete) {
        onComplete();
      }
    }
  };
  
  const setStepData = (stepId: string, data: any) => {
    setStepDataState((prev) => ({
      ...prev,
      [stepId]: data,
    }));
  };
  
  const goToNextStep = () => {
    if (canGoToNextStep()) {
      setActiveStep(activeStep + 1);
    }
  };
  
  const goToPreviousStep = () => {
    if (canGoToPreviousStep()) {
      setActiveStep(activeStep - 1);
    }
  };
  
  const canGoToNextStep = () => {
    if (activeStep >= steps.length - 1) return false;
    
    const nextStep = stepsState[activeStep + 1];
    return Boolean(nextStep?.isEnabled !== false);
  };
  
  const canGoToPreviousStep = () => {
    return activeStep > 0;
  };
  
  const isStepOptional = (stepIndex: number) => {
    return Boolean(stepsState[stepIndex]?.isOptional);
  };
  
  const isStepCompleted = (stepIndex: number) => {
    return Boolean(stepsState[stepIndex]?.isCompleted);
  };
  
  const isStepDisabled = (stepIndex: number) => {
    return Boolean(stepsState[stepIndex]?.isEnabled === false);
  };
  
  const isLastStep = () => {
    return activeStep === steps.length - 1;
  };
  
  const isFirstStep = () => {
    return activeStep === 0;
  };
  
  const contextValue = useMemo(
    () => ({
      steps: stepsState,
      activeStep,
      setActiveStep,
      completeStep,
      goToNextStep,
      goToPreviousStep,
      canGoToNextStep,
      canGoToPreviousStep,
      isStepOptional,
      isStepCompleted,
      isStepDisabled,
      isLastStep,
      isFirstStep,
      stepData,
      setStepData,
    }),
    [stepsState, activeStep, stepData]
  );
  
  return <StepperContext.Provider value={contextValue}>{children}</StepperContext.Provider>;
};
